import { CaretDownIcon, CaretRightIcon } from "@radix-ui/react-icons";

/**
 * SidebarNavItem - Navigation item component for sidebar
 *
 * Renders a clickable navigation item with icon, label, and optional
 * expand/collapse indicator for items with children. Supports active
 * state styling and hover effects.
 *
 * @param {Object} props - Component props
 * @param {React.Component} props.icon - Radix UI icon component
 * @param {string} props.label - Text label for the navigation item
 * @param {boolean} [props.active=false] - Whether this item is currently active
 * @param {boolean} [props.hasChildren=false] - Whether this item has child items
 * @param {boolean} [props.expanded=false] - Whether child items are expanded
 * @param {Function} [props.onClick] - Click handler function
 *
 * @returns {JSX.Element} Sidebar navigation item button
 */
export default function SidebarNavItem({
  icon: Icon,
  label,
  active = false,
  hasChildren = false,
  expanded = false,
  onClick,
}) {
  return (
    <button
      onClick={onClick}
      aria-current={active ? "page" : undefined}
      className={`
        flex items-center gap-3 w-full
        px-3 py-2.5 rounded-lg text-sm font-medium transition-colors
        ${
          active
            ? "bg-brand-600 text-white shadow-sm"
            : "text-brand-100/70 hover:text-white hover:bg-card/10"
        }
      `}
    >
      <Icon className={`w-[18px] h-[18px] shrink-0 ${active ? "text-white" : "text-brand-200/80"}`} />
      <span className="flex-grow text-left">{label}</span>
      {hasChildren &&
        (expanded ? (
          <CaretDownIcon className="w-4 h-4" />
        ) : (
          <CaretRightIcon className="w-4 h-4" />
        ))}
    </button>
  );
}
